import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Topbar from "./TopBar";
import NotificationDropdown from "./Notifications/NotificationDropdown";
import PreIPOPlatform from "./learningcenter";
import {
  Bell,
  GraduationCap,
  UserCheck,
  Wallet,
  Gift,
  LogOut,
  Heart,
  HeartHandshake,
  Save,
  User,
  CalendarCheck,
  Menu,
  X,
  Home,
  Info,
  Settings,
  FileText,
  BookOpen,
  Users,
  MessageSquare,
  HelpCircle,
  Mail,
  LogIn,
  ChevronDown,
  History,
  ClipboardList,
  Building2,
  LayoutDashboard,
  Banknote,
  PieChart,
} from "lucide-react";

const Navbar = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const menuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const adminDropdownRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userRole, logout } = useAuth();

  // Handle sticky navbar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest(".navbar-toggler")
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuRef]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(event.target)
      ) {
        setUserDropdownOpen(false);
      }
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target)
      ) {
        setAdminDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggleMobileMenu = (e) => {
    e && e.preventDefault();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = (e, id) => {
    e.preventDefault();
    setActiveDropdown(activeDropdown === id ? null : id);
  };



  // PUBLIC NAVBAR COMPONENT
  const PublicNavbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isSticky, setIsSticky] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const menuRef = useRef(null);
    const desktopDropdownRef = useRef(null);
    const mobileDropdownRef = useRef(null);

    useEffect(() => {
      const handleScroll = () => {
        setIsSticky(window.scrollY > 20);
      };

      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
          setIsMobileMenuOpen(false);
        }

        if (
          desktopDropdownRef.current &&
          !desktopDropdownRef.current.contains(event.target) &&
          (!mobileDropdownRef.current ||
            !mobileDropdownRef.current.contains(event.target))
        ) {
          setActiveDropdown(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleMobileMenu = () => {
      setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleDropdown = (e, name) => {
      e.preventDefault();
      setActiveDropdown(activeDropdown === name ? null : name);
    };

    // Determine if we are on a page with a transparent navbar overlay (Home, About, Services, Contact)
    const isHeroPage = ["/", "/about", "/services", "/contact"].includes(location.pathname);

    // Modern glassmorphism design - consistent across all pages
    // Outer navbar is now transparent to let the pill float
    const navbarBg = "bg-transparent";

    // Consistent dark text for better readability on glass effect
    const textColor = "text-gray-700";
    const hoverColor = "hover:text-[#0C7FA5]";

    // Always use black logo for consistency and better contrast
    const logoSrc = "/investhivelogo.webp";

    return (
      <>
        <nav
          className={`fixed w-full z-50 transition-all duration-300 ${navbarBg}`}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center h-24">

              {/* Unified Pill Container - Visible on Desktop */}
              <div className="hidden lg:flex items-center justify-between w-full bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-full px-4 py-3 transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">

                {/* Logo Section */}
                <Link to="/" className="flex items-center group px-2">
                  <img
                    src={logoSrc}
                    alt="InvestHive"
                    className="h-9 w-auto object-contain transition-all duration-300 group-hover:scale-105"
                  />
                </Link>

                <div className="h-8 w-px bg-gray-200 mx-2"></div>

                {/* Navigation Links */}
                <div className="flex items-center gap-1 flex-1 justify-center">
                  <Link to="/" className="px-6 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-[#0C7FA5] rounded-full transition-all duration-200">
                    Home
                  </Link>

                  <div className="h-5 w-px bg-gray-200/50"></div>

                  <Link to="/about" className="px-6 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-[#0C7FA5] rounded-full transition-all duration-200">
                    About
                  </Link>

                  <div className="h-5 w-px bg-gray-200/50"></div>

                  <Link to="/services" className="px-6 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-[#0C7FA5] rounded-full transition-all duration-200">
                    Services
                  </Link>

                  <div className="h-5 w-px bg-gray-200/50"></div>

                  {/* Pages Dropdown */}
                  {/* <div 
                  className="relative group"
                  ref={desktopDropdownRef}
                  onMouseEnter={() => setActiveDropdown("pages")}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button 
                    className={`flex items-center px-6 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-[#0C7FA5] rounded-full transition-all duration-200 ${activeDropdown === "pages" ? "bg-gray-50 text-[#0C7FA5]" : ""}`}
                    onClick={(e) => toggleDropdown(e, "pages")}
                  >
                    Pages
                    <ChevronDown size={16} strokeWidth={2} className={`ml-1.5 transition-transform duration-300 ${activeDropdown === "pages" ? "rotate-180" : ""}`} />
                  </button>

                  <div className={`absolute top-full left-1/2 -translate-x-1/2 w-56 pt-4 transition-all duration-300 ${
                    activeDropdown === "pages" ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-2"
                  }`}>
                    <div className="bg-white rounded-2xl shadow-[0_20px_50px_rgb(8,_112,_184,_0.1)] border border-gray-100 overflow-hidden py-2">
                      {[
                        { name: "Our Blog", path: "/blog" },
                        { name: "Testimonials", path: "/testimonials" },
                        { name: "FAQs", path: "/faqs" },
                      ].map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className="block px-5 py-3 text-sm text-gray-600 hover:text-[#0C7FA5] hover:bg-blue-50/50 transition-all duration-200 first:pt-3 last:pb-3"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div> */}

                  <div className="h-5 w-px bg-gray-200/50"></div>

                  <Link to="/contact" className="px-6 py-2.5 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-[#0C7FA5] rounded-full transition-all duration-200">
                    Contact
                  </Link>
                </div>

                <div className="h-8 w-px bg-gray-200 mx-2"></div>

                {/* Auth Buttons */}
                <div className="flex items-center gap-2 px-2">
                  <button
                    onClick={() => navigate("/Signin-Signup", { state: { showRegister: false } })}
                    className="px-6 py-2.5 text-sm font-semibold text-gray-700 hover:text-[#0C7FA5] hover:bg-gray-50 rounded-full transition-all duration-200"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate("/Signin-Signup", { state: { showRegister: true } })}
                    className="px-7 py-3 bg-[#0C7FA5] text-white text-sm font-semibold rounded-full shadow-lg hover:shadow-xl hover:bg-[#0a6c8d] transition-all duration-300 transform hover:-translate-y-0.5"
                  >
                    Get Started
                  </button>
                </div>
              </div>

              {/* Mobile View - Logo and Menu Button */}
              <div className="lg:hidden w-full flex justify-between items-center bg-white/90 backdrop-blur-md p-3 mt-3 rounded-2xl shadow-sm border border-gray-100">
                <Link to="/" className="flex items-center group">
                  <img
                    src={logoSrc}
                    alt="InvestHive"
                    className="h-9 w-auto object-contain"
                  />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(true)}
                  className="p-2 text-gray-700 hover:text-[#0C7FA5] transition-all duration-200 hover:bg-white/50 rounded-lg"
                >
                  <Menu size={24} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          <div
            className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${isMobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Mobile Menu Sidebar - Pill Design */}
          <div
            ref={menuRef}
            className={`lg:hidden fixed right-0 top-0 h-screen w-[300px] bg-white/95 backdrop-blur-xl shadow-2xl border-l border-gray-200/50 z-[60] transform transition-transform duration-300 ease-out rounded-l-3xl ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
              }`}
          >
            <div className="flex flex-col h-full">
              {/* Header with Logo */}
              <div className="p-6 flex justify-between items-center border-b border-gray-200/50 bg-gradient-to-r from-[#44D2F6]/5 to-[#0C7FA5]/5">
                <img
                  src="/investhivelogo.webp"
                  alt="InvestHive"
                  className="h-9 w-auto"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600 hover:text-gray-800 transition-all duration-200"
                >
                  <X size={20} strokeWidth={1.5} />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="flex-1 overflow-y-auto py-6 px-4 space-y-2">
                {[
                  { name: "Home", path: "/", icon: Home },
                  { name: "About", path: "/about", icon: Info },
                  { name: "Services", path: "/services", icon: Settings },
                ].map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === link.path
                      ? "bg-gradient-to-r from-[#44D2F6]/20 to-[#0C7FA5]/20 text-[#0C7FA5] shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#102147]"
                      }`}
                  >
                    <link.icon size={18} strokeWidth={1.5} className="mr-3" />
                    {link.name}
                  </Link>
                ))}

                {/* Pages Dropdown */}
                {/* <div className="pt-2">
                  <button
                    onClick={(e) => toggleDropdown(e, "mobile-pages")}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${activeDropdown === "mobile-pages"
                      ? "bg-gradient-to-r from-[#44D2F6]/20 to-[#0C7FA5]/20 text-[#0C7FA5] shadow-sm"
                      : "text-gray-600 hover:bg-gray-50 hover:text-[#102147]"
                      }`}
                  >
                    <div className="flex items-center">
                      <FileText size={18} strokeWidth={1.5} className="mr-3" />
                      Pages
                    </div>
                    <ChevronDown
                      size={16}
                      strokeWidth={1.5}
                      className={`transition-transform duration-300 ${activeDropdown === "mobile-pages" ? "rotate-180" : ""
                        }`}
                    />
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-300 ${activeDropdown === "mobile-pages" ? "max-h-48 opacity-100 mt-2" : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="pl-4 space-y-1 ml-4 border-l-2 border-[#44D2F6]/30">
                      {[
                        { name: "Our Blog", path: "/blog" },
                        { name: "Testimonials", path: "/testimonials" },
                        { name: "FAQs", path: "/faqs" },
                      ].map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-600 hover:text-[#0C7FA5] hover:bg-[#44D2F6]/5 rounded-lg transition-all duration-200"
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div> */}

                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${location.pathname === "/contact"
                    ? "bg-gradient-to-r from-[#44D2F6]/20 to-[#0C7FA5]/20 text-[#0C7FA5] shadow-sm"
                    : "text-gray-600 hover:bg-gray-50 hover:text-[#102147]"
                    }`}
                >
                  <Mail size={18} strokeWidth={1.5} className="mr-3" />
                  Contact
                </Link>

              </div>

              {/* Footer CTA */}
              <div className="p-6 border-t border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-blue-50/30">
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    navigate("/Signin-Signup", { state: { showRegister: true } });
                  }}
                  className="w-full bg-gradient-to-r from-[#44D2F6] to-[#0C7FA5] text-white py-3.5 px-4 rounded-full text-sm font-bold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                >
                  Start Investing
                </button>
                <p className="text-center mt-4 text-xs text-gray-500">
                  Already have an account?{" "}
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      navigate("/Signin-Signup", { state: { showRegister: false } });
                    }}
                    className="text-[#0C7FA5] font-semibold hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </nav>
        {!isHeroPage && <div className="h-24"></div>}
      </>
    );
  };

  // User Navbar Component

  const UserNavbar = () => {
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const userDropdownRef = useRef(null);
    const menuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          userDropdownRef.current &&
          !userDropdownRef.current.contains(event.target)
        ) {
          setUserDropdownOpen(false);
        }
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          isMobileMenuOpen
        ) {
          setIsMobileMenuOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [isMobileMenuOpen]);

    const toggleMobileMenu = () => {
      setIsMobileMenuOpen((prev) => !prev);
    };



    const handleLogout = () => {
      logout();
      navigate("/Signin-Signup");
    };

    const navigateToLearningCenter = () => {
      navigate("/learning-center");
    };

    const isActive = (path) => {
      return (
        location.pathname === path ||
        (path !== "/" && location.pathname.startsWith(path))
      );
    };

    const navItems = [
      { path: "/dashboard", label: "Unlisted Shares" },
      { path: "/portfolio", label: "Portfolio" },
      // { path: "/mutual-funds", label: "Mutual Fund" },
      { path: "/appointments", label: "Appointments" },
      { path: "/bonds-listing", label: "Bonds" },
    ];

    const userMenuItems = [
      {
        path: "/profile/kyc",
        icon: <UserCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "User And KYC Details",
      },
      {
        path: "/myScreener",
        icon: <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Saved Screeners",
      },
      {
        path: "/myWatchlist",
        icon: <Heart className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Watchlist",
      },
      {
        path: "/myappointments",
        icon: <CalendarCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Your Appointments",
      },
      // {
      //   path: "/support",
      //   icon: <HelpCircle className="w-4 h-4 mr-2" />,
      //   label: "Support",
      // },
      {
        path: "/wallet",
        icon: <Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Wallet",
      },
      {
        path: "/orderHistory",
        icon: <History className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Investment History",
      },
      {
        path: "/referral",
        icon: <Gift className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Refer & Earn",
      },
      {
        path: "/orders",
        icon: <ClipboardList className="w-4 h-4 mr-2" strokeWidth={1.5} />,
        label: "Order Request",
      },
    ];

    return (
      <>
        <nav className="fixed w-full z-50 transition-all duration-300 bg-transparent">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center items-center h-24">

              {/* Unified Pill Container - Desktop */}
              <div className="hidden lg:flex items-center justify-between w-full bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-full px-4 py-3 transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">

                {/* Logo Section */}
                <Link to="/dashboard" className="flex items-center group px-2">
                  <img
                    src="/investhivelogo.webp"
                    alt="InvestHive"
                    className="h-9 w-auto object-contain transition-all duration-300 group-hover:scale-105"
                  />
                </Link>

                <div className="h-8 w-px bg-gray-200 mx-2"></div>

                {/* Navigation Items */}
                <div className="flex items-center gap-1 flex-1 justify-center">
                  {navItems.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`px-6 py-2.5 text-base font-medium rounded-full transition-all duration-200 ${active
                          ? "text-blue-600 bg-blue-50 font-semibold"
                          : "text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                          }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                </div>

                <div className="h-8 w-px bg-gray-200 mx-2"></div>

                {/* Right Section: Notification, Learning, Profile */}
                <div className="flex items-center gap-2 px-2">
                  <NotificationDropdown />

                  <button
                    className="education-button flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-all duration-200 border border-transparent hover:border-blue-100"
                    onClick={navigateToLearningCenter}
                    title="Learning Center"
                  >
                    <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
                  </button>

                  {/* User dropdown */}
                  <div ref={userDropdownRef} className="relative">
                    <button
                      className="flex items-center focus:outline-none group"
                      onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    >
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-50 group-hover:bg-blue-50 text-gray-600 group-hover:text-blue-600 transition-all duration-200 border border-transparent group-hover:border-blue-100">
                        <User className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                    </button>

                    {userDropdownOpen && (
                      <div className="absolute right-0 mt-3 w-72 rounded-2xl shadow-xl py-2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transform transition-all duration-200 ease-out z-50 border border-gray-100">
                        <div className="px-4 py-3 border-b border-gray-100 mb-2">
                          <p className="text-sm font-semibold text-gray-900">My Account</p>
                          <p className="text-xs text-gray-500 mt-0.5">Manage your profile & settings</p>
                        </div>
                        <div className="py-1 px-2">
                          {userMenuItems.map((item) => {
                            const active = isActive(item.path);
                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                onClick={() => setUserDropdownOpen(false)}
                                className={`flex items-center w-full text-left px-4 py-2.5 text-sm rounded-xl transition-colors duration-200 ${active
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                                  }`}
                              >
                                <span className={`mr-3 ${active ? "text-blue-600" : "text-gray-400"}`}>{item.icon}</span>
                                {item.label}
                              </Link>
                            );
                          })}
                        </div>
                        <div className="border-t border-gray-100 mt-2 pt-2 px-2 pb-2">
                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200 font-medium"
                          >
                            <LogOut className="w-4 h-4 mr-3" strokeWidth={1.5} />
                            Log out
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile View - Logo and Menu Button */}
              <div className="lg:hidden w-full flex justify-between items-center bg-white/90 backdrop-blur-md p-3 mt-3 rounded-2xl shadow-sm border border-gray-100">
                <Link to="/dashboard" className="flex items-center group">
                  <img
                    src="/investhivelogo.webp"
                    alt="InvestHive"
                    className="h-9 w-auto object-contain"
                  />
                </Link>

                <div className="flex items-center gap-2">
                  <NotificationDropdown />

                  <div className="h-6 w-px bg-gray-200 mx-1"></div>

                  <button
                    onClick={toggleMobileMenu}
                    className="p-2 text-gray-700 hover:text-blue-600 transition-all duration-200 hover:bg-white/50 rounded-lg"
                  >
                    {isMobileMenuOpen ? (
                      <X size={24} strokeWidth={1.5} />
                    ) : (
                      <Menu size={24} strokeWidth={1.5} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>


          {/* Mobile Menu Dropdown - Pill Design */}
          {isMobileMenuOpen && (
            <div
              className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-xl rounded-b-3xl mx-4 mt-2 max-h-[calc(100vh-120px)] overflow-y-auto"
              id="mobile-menu"
              ref={menuRef}
            >
              <div className="px-4 py-3 space-y-1">
                {/* User Menu Items */}
                <p className="px-5 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  My Account
                </p>
                {userMenuItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center w-full text-left px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${active
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        }`}
                    >
                      <span className={`mr-3 ${active ? "text-blue-600" : "text-gray-400"}`}>
                        {item.icon}
                      </span>
                      {item.label}
                    </Link>
                  );
                })}

                {/* Logout */}
                <div className="pt-2 mt-2 border-t border-gray-100">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-full transition-colors duration-200 font-medium"
                  >
                    <LogOut className="w-4 h-4 mr-3" strokeWidth={1.5} />
                    Log out
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
        <div className="h-24"></div>
      </>
    );
  };
  // Admin Navbar Component
  const AdminNavbar = () => {
    const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const adminDropdownRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          adminDropdownRef.current &&
          !adminDropdownRef.current.contains(event.target)
        ) {
          setAdminDropdownOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleMenuItem = (e, path) => {
      e.preventDefault();
      setAdminDropdownOpen(false);
      navigate(path);
    };

    const handleLogout = () => {
      logout(); // Call the logout function from useAuth
      navigate("/Signin-Signup"); // Redirect to the login page
    };

    // Helper function to check if the current path matches or starts with a menu item path
    const isActive = (path) => {
      return (
        location.pathname === path ||
        (path !== "/" && location.pathname.startsWith(path))
      );
    };

    return (
      <>
        <nav
          className="bg-white fixed left-0 right-0 w-full z-50 mx-auto max-w-8xl shadow-lg transition-all duration-300 hover:bg-gray-50"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Link to="/admindashboard" className="flex items-center">
                    <img
                      src="/investhivelogo.webp"
                      alt="InvestHive"
                      className="h-9 sm:h-10 md:h-10 lg:h-10 w-auto object-contain"
                    />
                  </Link>
                </div>
              </div>

              {/* Mobile: Dashboard Button Removed */}

              {/* Desktop: Nav Links Removed */}

              {/* Right side: Admin profile */}
              <div className="flex items-center space-x-4">
                {/* Admin Profile Dropdown */}
                <div ref={adminDropdownRef} className="relative ml-3">
                  <button
                    className="flex items-center space-x-3 focus:outline-none"
                    onClick={() => setAdminDropdownOpen(!adminDropdownOpen)}
                  >
                    <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-200">
                      <User className="w-5 h-5 text-gray-700" strokeWidth={1.5} />
                    </div>
                  </button>

                  {/* Admin Dropdown Menu */}
                  {adminDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none transform transition-all duration-200 ease-out z-50">
                      <div className="py-1">
                        <Link
                          to="/AdminProfile"
                          onClick={() => setAdminDropdownOpen(false)}
                          className={`flex items-center w-full text-left px-4 py-2 text-sm transition-colors duration-200
                          ${isActive("/AdminProfile")
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            }`}
                        >
                          <span className="text-gray-500">
                            <UserCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                          </span>
                          Admin Profile
                        </Link>
                      </div>
                      <div className="border-t border-gray-100 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                        >
                          <LogOut className="w-4 h-4 mr-2 text-red-600" strokeWidth={1.5} />
                          Log out
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile menu button removed */}
              </div>
            </div>
          </div>

          {/* Mobile menu removed */}
        </nav>

        {/* Spacer div to push content below fixed navbar */}
        <div className="h-16"></div>
      </>
    );
  };
  // Expert navbar placeholder
  const ExpertNavbar = () => {
    return <div>Expert Dashboard - Not implemented as requested</div>;
  };

  // Render the appropriate navbar
  const renderNavbar = () => {
    if (!isAuthenticated) {
      return <PublicNavbar />;
    }

    switch (userRole) {
      case "admin":
        return <AdminNavbar />;
      case "expert":
        return <ExpertNavbar />;
      default:
        return <UserNavbar />;
    }
  };
  return (
    <>
      {renderNavbar()}

      {/* Mobile Footer Navigation - Only for logged-in regular users */}
      {isAuthenticated && userRole === "user" && (
        <div className="md:hidden fixed bottom-3 left-4 right-4 bg-white/90 backdrop-blur-md border border-gray-200/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-full z-50">
          <div className="flex justify-around items-center py-2 px-2">
            <Link
              to="/screener"
              className={`flex flex-col items-center text-xs transition-colors duration-200 ${location.pathname === "/screener" ? "text-blue-600" : "text-gray-500 hover:text-blue-600"
                }`}
            >
              <Building2 className="text-xl mb-0.5" strokeWidth={1.5} />
              <span className="font-medium">Companies</span>
            </Link>

            <Link
              to="/dashboard"
              className={`flex flex-col items-center text-xs transition-colors duration-200 ${location.pathname === "/dashboard" ? "text-blue-600" : "text-gray-500 hover:text-blue-600"
                }`}
            >
              <LayoutDashboard className="text-xl mb-0.5" strokeWidth={1.5} />
              <span className="font-medium">Dashboard</span>
            </Link>

            <Link
              to="/bonds-listing"
              className={`flex flex-col items-center text-xs transition-colors duration-200 ${location.pathname === "/bonds-listing" ? "text-blue-600" : "text-gray-500 hover:text-blue-600"
                }`}
            >
              <Banknote className="text-xl mb-0.5" strokeWidth={1.5} />
              <span className="font-medium">Bonds</span>
            </Link>

            <Link
              to="/portfolio"
              className={`flex flex-col items-center text-xs transition-colors duration-200 ${location.pathname === "/portfolio" ? "text-blue-600" : "text-gray-500 hover:text-blue-600"
                }`}
            >
              <PieChart className="text-xl mb-0.5" strokeWidth={1.5} />
              <span className="font-medium">Portfolio</span>
            </Link>
          </div>
        </div>
      )}

      {/* Mobile Footer for Admin */}
      {isAuthenticated && userRole === "admin" && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white mt-3 border-t shadow z-50">
          <div className="flex justify-around items-center py-2">
            {/* <Link
              to="/admindashboard"
              className="flex flex-col items-center text-xs text-gray-700 hover:text-blue-600"
            >
              <MdDashboard className="text-xl mb-1" />
              <span>Dashboard</span>
            </Link> */}
          </div>
        </div>
      )}

      {/* Mobile Footer for Expert */}
      {isAuthenticated && userRole === "expert" && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white mt-3 border-t shadow z-50">
          <div className="flex justify-around items-center py-2">
            <Link
              to="/expert-dashboard"
              className="flex flex-col items-center text-xs text-gray-700 hover:text-blue-600"
            >
              <LayoutDashboard className="text-xl mb-1" strokeWidth={1.5} />
              <span>Dashboard</span>
            </Link>
            <Link
              to="/appointments-management"
              className="flex flex-col items-center text-xs text-gray-700 hover:text-blue-600"
            >
              <CalendarCheck className="text-xl mb-1" />
              <span>Appointments</span>
            </Link>
            <Link
              to="/client-portfolios"
              className="flex flex-col items-center text-xs text-gray-700 hover:text-blue-600"
            >
              <PieChart className="text-xl mb-1" strokeWidth={1.5} />
              <span>Clients</span>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};
export default Navbar;
